import React, { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { Button, Stack } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  ExpenseFragment,
  CreateExpenseDocument,
  CreateExpenseMutation,
  CreateExpenseMutationVariables,
  SetExpenseUnitAssignmentsDocument,
  SetExpenseUnitAssignmentsMutation,
  SetExpenseUnitAssignmentsMutationVariables,
  CreateMoneyTransactionDocument,
  CreateMoneyTransactionMutation,
  CreateMoneyTransactionMutationVariables,
  CreateExpensePaymentDocument,
  CreateExpensePaymentMutation,
  CreateExpensePaymentMutationVariables,
} from '../../../generated/graphql';
import { useNavigate } from 'react-router';
import { appPaths } from '../../../app-paths';
import { useAlert } from '@dwelvio/react-alert';
import { useApp } from '../../../components/app-context';
import { NewExpenseDialog } from '../../../components/new-expense-dialog';
import { NewCashExpenseDialog } from '../../../components/new-cash-expense-dialog';
import CircleIcon from '@mui/icons-material/Circle';
import { AddIcon } from '../../../components/icons';

interface UnitExpensesSectionProps {
  unitId: string;
  expenses: ExpenseFragment[];
  disabled?: boolean;
  onRefresh?: () => void;
}

export function UnitExpensesSection({ unitId, expenses, disabled, onRefresh }: UnitExpensesSectionProps) {
  const navigate = useNavigate();
  const app = useApp();
  const { show } = useAlert();
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showCashDialog, setShowCashDialog] = useState(false);

  const [createExpense, { loading: createLoading }] = useMutation<
    CreateExpenseMutation,
    CreateExpenseMutationVariables
  >(CreateExpenseDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa vytvoriť výdavok.', e.message),
  });

  const [setUnitAssignments] = useMutation<
    SetExpenseUnitAssignmentsMutation,
    SetExpenseUnitAssignmentsMutationVariables
  >(SetExpenseUnitAssignmentsDocument, {
    onError: (e: Error) => show.error('Výdavok bol vytvorený, ale nepodarilo sa priradiť k nehnuteľnosti.', e.message),
  });

  const [createTransaction, { loading: transactionLoading }] = useMutation<
    CreateMoneyTransactionMutation,
    CreateMoneyTransactionMutationVariables
  >(CreateMoneyTransactionDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa vytvoriť transakciu.', e.message),
  });

  const [createExpensePayment, { loading: paymentLoading }] = useMutation<
    CreateExpensePaymentMutation,
    CreateExpensePaymentMutationVariables
  >(CreateExpensePaymentDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa priradiť platbu k výdavku.', e.message),
  });

  const anyLoading = createLoading || transactionLoading || paymentLoading;

  const isOverdue = (expense: ExpenseFragment) => {
    if (expense.paidAt || expense.isCost) return false;
    return new Date(expense.dueDate) < new Date();
  };

  const columns = React.useMemo(
    (): GridColDef<ExpenseFragment>[] => [
      {
        field: 'overdue',
        headerName: '',
        width: 50,
        sortable: false,
        renderCell: (params) =>
          isOverdue(params.row) ? <CircleIcon style={{ color: 'red', fontSize: 'small' }} /> : null,
      },
      {
        field: 'date',
        headerName: 'Dátum',
        width: 130,
        valueFormatter: (value: string) => app.formatDate(value),
        getSortComparator: (sortDirection) => (v1, v2, p1, p2) => {
          const t1 = new Date(v1 as string).getTime();
          const t2 = new Date(v2 as string).getTime();
          if (t1 !== t2) return sortDirection === 'desc' ? t2 - t1 : t1 - t2;
          const c1 = new Date((p1.api.getRow(p1.id) as { createdAt: string }).createdAt).getTime();
          const c2 = new Date((p2.api.getRow(p2.id) as { createdAt: string }).createdAt).getTime();
          return sortDirection === 'desc' ? c2 - c1 : c1 - c2;
        },
      },
      { field: 'title', headerName: 'Názov', flex: 1, minWidth: 150 },
      {
        field: 'amount',
        headerName: 'Celková suma',
        width: 130,
        valueFormatter: (value: number) => app.formatNumber(value, 2) + ' €',
      },
      {
        field: 'unitAssignments',
        headerName: 'Priradená suma',
        width: 140,
        sortable: false,
        valueGetter: (_value: unknown, row: ExpenseFragment) => {
          const assignment = row.unitAssignments?.find((a) => a.unitId === unitId);
          return assignment?.amount ?? null;
        },
        valueFormatter: (value: number | null) => (value != null ? app.formatNumber(value, 2) + ' €' : '—'),
      },
    ],
    [app, unitId],
  );

  /** Assign the newly created expense 100% to this unit */
  const assignToUnit = async (expenseId: string, amount: number): Promise<boolean> => {
    const assignResult = await setUnitAssignments({
      variables: { expenseId, assignments: [{ unitId, amount }] },
    });
    return !!assignResult.data?.setExpenseUnitAssignments?.expense;
  };

  /** Create an invoice expense (no automatic payment) */
  const handleInvoiceSubmit = async (input: {
    title: string;
    amount: number;
    taxDeductible: number;
    date: string;
    dueDate?: string;
    refNo?: string;
    isCost?: boolean;
  }) => {
    const result = await createExpense({ variables: { input } });
    const createdId = result.data?.createExpense?.expense?.id;
    if (!createdId) return;

    const assigned = await assignToUnit(createdId, input.amount);
    setShowInvoiceDialog(false);
    onRefresh?.();

    if (assigned) {
      show.info('Výdavok bol úspešne vytvorený a priradený k nehnuteľnosti.');
    } else {
      show.error('Výdavok bol vytvorený, ale nepodarilo sa priradiť k nehnuteľnosti. Prosím, priraďte manuálne.');
      navigate(appPaths.expense.navigate({ expenseId: createdId }));
    }
  };

  /** Create a cash expense: expense + DEBIT cash transaction + expense payment */
  const handleCashSubmit = async (input: { title: string; amount: number; taxDeductible: number; date: string }) => {
    // 1. Create the expense
    const expenseResult = await createExpense({
      variables: {
        input: {
          title: input.title,
          amount: input.amount,
          taxDeductible: input.taxDeductible,
          date: input.date,
          dueDate: input.date,
          isCost: false,
        },
      },
    });
    const createdExpense = expenseResult.data?.createExpense?.expense;
    if (!createdExpense) return;

    // 2. Assign to this unit
    await assignToUnit(createdExpense.id, input.amount);

    // 3. Create a DEBIT cash transaction
    const txResult = await createTransaction({
      variables: {
        input: {
          transactionDate: input.date,
          type: 'DEBIT',
          value: input.amount,
          cash: true,
          bankAccountNo: 'cash',
        },
      },
    });
    const createdTransaction = txResult.data?.createMoneyTransaction?.moneyTransaction;
    if (!createdTransaction) {
      show.error('Výdavok bol vytvorený, ale nepodarilo sa vytvoriť platobnú transakciu.');
      setShowCashDialog(false);
      onRefresh?.();
      navigate(appPaths.expense.navigate({ expenseId: createdExpense.id }));
      return;
    }

    // 4. Link transaction to expense as payment
    const paymentResult = await createExpensePayment({
      variables: {
        input: { expenseId: createdExpense.id, transactionId: createdTransaction.id, amount: input.amount },
      },
    });

    setShowCashDialog(false);
    onRefresh?.();

    if (paymentResult.data?.createExpensePayment?.errors?.length) {
      show.error('Výdavok a transakcia boli vytvorené, ale nepodarilo sa ich prepojiť. Prosím, priraďte manuálne.');
    } else {
      show.info('Hotovostný výdavok bol úspešne vytvorený a zaplatený.');
    }
  };

  return (
    <Stack direction="column" width="100%" sx={{ height: 440 }} spacing={2}>
      <Stack direction="row" spacing={1} justifyContent="flex-start">
        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setShowCashDialog(true)} disabled={disabled}>
          Hotovostný výdavok
        </Button>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setShowInvoiceDialog(true)}
          disabled={disabled}
        >
          Výdavok (faktúra)
        </Button>
      </Stack>
      <DataGrid
        rows={expenses}
        columns={columns}
        initialState={{
          pagination: { paginationModel: { pageSize: 5 } },
          sorting: { sortModel: [{ field: 'date', sort: 'desc' }] },
        }}
        pageSizeOptions={[5, 10, 20]}
        disableRowSelectionOnClick
        onRowClick={(params) => navigate(appPaths.expense.navigate({ expenseId: params.row.id }))}
        sx={{ cursor: 'pointer' }}
      />

      {!disabled && (
        <>
          <NewCashExpenseDialog
            open={showCashDialog}
            onClose={() => setShowCashDialog(false)}
            onSubmit={handleCashSubmit}
            loading={anyLoading}
          />
          <NewExpenseDialog
            open={showInvoiceDialog}
            onClose={() => setShowInvoiceDialog(false)}
            onSubmit={handleInvoiceSubmit}
            loading={anyLoading}
          />
        </>
      )}
    </Stack>
  );
}
